import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/record-table.css';
import './styles/structured-source.css';
import sourceCss from './styles/structured-source.css?raw';
import recordTableCss from './styles/record-table.css?raw';
import tokensCss from './styles/tokens.css?raw';

import { header } from './components/header.js';
import { hero } from './components/hero.js';
import { tool } from './components/tool.js';
import { results } from './components/results.js';
import { footer } from './components/footer.js';
import { initPromo } from './components/promo.js';
import { initTheme } from './lib/theme.js';
import { track, trackClientError, trackUnsupportedFormat, trackPageView, trackVisitRecency } from './lib/analytics.js';
import { initInput } from './ui/input.js';
import { showNotice } from './ui/notice-dialog.js';
import { mountRecordVirtualization } from './ui/record-virtual.js';
import { compare } from './lib/worker-client.js';
import { formatContent } from './lib/format.js';
import { copyShareLink } from './lib/clipboard.js';
import { encodeState, decodeState } from './lib/share.js';
import { unifiedPatch, htmlReport, changeListJson, download } from './lib/export.js';
import { renderText } from './render/text-view.js';
import { renderStructuredSource } from './render/structured-source-view.js';
import { renderStructural } from './render/structural-view.js';
import { renderRecords } from './render/records-view.js';
import { renderProse } from './render/prose-view.js';
import { drawRibbons } from './render/ribbon.js';
import { summaryHtml, cruxHtml, cruxSubtitle } from './render/summary.js';
import { changeStops } from './engine/change-stops.js';
import { createChangeNav } from './ui/change-nav.js';
import { analyzeChanges, ANALYSIS_THRESHOLDS } from './engine/change-analysis.js';
import { analysisHtml, analysisLead } from './render/analysis.js';
import { esc } from './render/escape.js';
import { NARROW_QUERY, effectiveViewFor, RECORD_TABLE_QUERY, recordLayoutFor } from './lib/responsive.js';

const $ = (s) => document.querySelector(s);
const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

initTheme();

// ---- compose the shell ----
$('#app').innerHTML = `
  ${header()}
  <main class="wrap workspace">
    ${hero()}
    ${tool()}
    ${results()}
  </main>
  ${footer()}
  <div id="toast" role="status"></div>
`;

// Keep move navigation local: do not replace a shared diff's URL fragment.
$('#diffBody').addEventListener('click', (event) => {
  const link = event.target.closest('.source-move');
  if (!link) return;
  const target = document.getElementById(link.getAttribute('href').slice(1));
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
  target.focus({ preventScroll: true });
});

// ---- toast ----
const toast = $('#toast');
let toastTimer;
function toastMsg(html) {
  toast.innerHTML = html;
  toast.classList.add('on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('on'), 3200);
}


// House promo: impression + click, creative id as `kind` (page events only).
initPromo();

// ---- input layer ----
// Funnel signals, de-noised: `input_provided` once per session when content first
// appears; `format_detected` only when the detected format actually changes
// (onChange fires on every keystroke, which we must not beacon).
let inputSeen = false;
let lastFmt = null;
// Change Analysis session state — declared before initInput, which detects (and
// therefore fires onChange) during initialisation. resetAnalysisSession() runs on
// that first detection, so EVERY binding it touches has to be initialised by the
// time initInput is called, not merely declared somewhere in the module (G59).
// Which disclosures were opened, once per section per comparison: a re-render
// re-creates the elements, so without this a rerun would look like fresh interest.
const analysisOpened = new Set();
let analysisShown = false;
let analysisBaseline = null;
let analysisBelow = false;

const input = initInput({
  initial: 'text',
  onUnsupported: trackUnsupportedFormat,
  onDataset: (shape) => track('dataset_load', { kind: shape }),
  onChange: ({ format, overridden, mismatch }) => {
    resetAnalysisSession(); // new inputs — the panel starts from scratch (spec §8.7)
    const hasContent = format === 'parquet' || !!($('#leftTa').value.trim() || $('#rightTa').value.trim());
    if (!hasContent) return;
    if (!inputSeen) {
      inputSeen = true;
      track('input_provided', { format });
    }
    if (format !== lastFmt) {
      lastFmt = format;
      track('format_detected', { format, extra: mismatch ? 'mismatch' : overridden ? 'manual' : 'auto' });
    }
  },
});

let lastResult = null;
let lastMeta = {};
let lastRibbons = [];
let view = 'sxs';
let expandJsonView = true;
let wrap = true; // wrap long lines by default so the two panes stay aligned
let scope = 'changed'; // records scope: 'changed' (default) | 'all' (spec §8.5/8.6)
let scopeActive = false; // the scope switcher only shows for large record sets
let unmountRecordVirtualization = () => {};

const SCOPE_MIN_ROWS = 50; // below this, scanning the ledger directly beats a toggle
// Side-by-side is unreadable at phone widths (5 columns @ 11px), so it renders as
// Unified below this width; the user's choice is preserved for when they rotate/resize.
const NARROW = matchMedia(NARROW_QUERY);
const RECORD_DESKTOP = matchMedia(RECORD_TABLE_QUERY);
const effectiveView = () => effectiveViewFor(view, NARROW.matches);

// Compare button state. `IDLE_LABEL` is captured once (never re-read from the
// button — reading a mutated "Comparing…" back was what left it stuck). A pending
// counter resets the button only when ALL in-flight runs finish; a token lets a
// newer run supersede an older one so stale results can't overwrite fresh ones.
const IDLE_LABEL = $('#compareBtn').textContent;
let pendingCompares = 0;
let compareToken = 0;

// The loader is DELAYED, not immediate. Nearly every comparison finishes in a
// few milliseconds, and a spinner that appears and disappears inside that window
// reads as a glitch rather than as progress — so nothing is shown until a run has
// already proved itself slow. After COMPARE_LOADER_SLOW the note says WHY it is
// slow, which on this tool is a real answer and not an apology: the work is
// happening on the reader's own machine, so size and hardware are the whole story.
const COMPARE_LOADER_DELAY = 350;
const COMPARE_LOADER_SLOW = 6000;
const LOADER_NOTE = 'Running in this tab — nothing is uploaded.';
const LOADER_NOTE_SLOW =
  'Large inputs take longer. This runs entirely on your device, so it depends on your machine, not on a server.';
let loaderTimer = 0;
let loaderSlowTimer = 0;

function showLoader() {
  // Already counting down or already up: a second concurrent run must not push
  // the countdown out by another 350ms.
  if (loaderTimer || !$('#compareLoader').hidden) return;
  loaderTimer = setTimeout(() => {
    loaderTimer = 0;
    $('#compareLoaderNote').textContent = LOADER_NOTE;
    $('#compareLoader').hidden = false;
    $('#results').classList.add('busy');
    loaderSlowTimer = setTimeout(() => {
      loaderSlowTimer = 0;
      $('#compareLoaderNote').textContent = LOADER_NOTE_SLOW;
    }, COMPARE_LOADER_SLOW);
  }, COMPARE_LOADER_DELAY);
}

function hideLoader() {
  clearTimeout(loaderTimer);
  clearTimeout(loaderSlowTimer);
  loaderTimer = 0;
  loaderSlowTimer = 0;
  $('#compareLoader').hidden = true;
  $('#results').classList.remove('busy');
}

function setBusy(on) {
  const btn = $('#compareBtn');
  btn.disabled = on;
  btn.textContent = on ? 'Comparing…' : IDLE_LABEL;
  // aria-busy is immediate even though the visible loader is delayed: a screen
  // reader user gets no button animation to infer from.
  const results = $('#results');
  if (on) {
    results.setAttribute('aria-busy', 'true');
    showLoader();
  } else {
    results.removeAttribute('aria-busy');
    hideLoader();
  }
}

// Merge the input payload with the ignore-fields list (Phase 9).
function buildPayload() {
  const payload = input.getPayload();
  const fields = $('#ignoreFields').value.split(',').map((s) => s.trim()).filter(Boolean);
  payload.options = { ...payload.options, ignoreFields: fields };
  return payload;
}

// ---- Compare (real worker round-trip) ----
// `report` distinguishes a user-initiated comparison (button / sample / shared
// link) from an in-place rerun (ignore-rule toggle, key column) so the funnel
// `compare_result` isn't inflated by every option change.
async function doCompare({ scroll = true, report = false } = {}) {
  if (input.isLoading()) { toastMsg('Please wait for the file to finish loading.'); return; }
  if (!input.validateSize()) return;
  const payload = buildPayload();
  $('#comparisonError').hidden = true;
  if (!payload.leftDocument && !payload.rightDocument && !payload.left.trim() && !payload.right.trim()) {
    toastMsg('Paste something to compare — or hit <span class="k">Try with sample</span>.');
    return;
  }
  const myToken = ++compareToken;
  pendingCompares++;
  setBusy(true);
  try {
    const result = await compare(payload);
    if (myToken !== compareToken) return; // a newer run superseded this one — don't render stale
    if (payload.format === 'parquet') {
      const current = input.getPayload();
      if (current.leftDocument !== payload.leftDocument || current.rightDocument !== payload.rightDocument || input.isLoading()) return;
    }
    lastMeta = { leftLabel: $('#leftFile').textContent || 'original', rightLabel: $('#rightFile').textContent || 'changed' };
    if (result.fallback && report) trackUnsupportedFormat(result.fallback.from);
    if (result.stub) {
      lastResult = null;
      withRenderErrorBeacon(() => renderStubResult(result));
      if (report) track('compare_result', { format: payload.format, kind: 'stub' });
    } else {
      lastResult = result;
      withRenderErrorBeacon(() => renderResult(result, { scroll, ignoreFields: payload.options.ignoreFields }));
      if (report) {
        // Scale as a BUCKET, never the row count: a raw total is content-adjacent
        // for a dataset someone can recognise, and buckets are what a trend needs.
        track('compare_size', { kind: sizeBucket(result) });
        track('compare_result', {
          format: payload.format,
          kind: result.render?.kind || 'text',
          extra: result.moved ? 'moved' : result.typeChanges ? 'typebreak' : 'plain',
          v: result.changes?.length ?? 0, // a count, never content
        });
      }
    }
  } catch (err) {
    if (myToken !== compareToken) return;
    if (payload.format === 'parquet') {
      $('#comparisonError').textContent = `Comparison not completed: ${err.message}`;
      $('#comparisonError').hidden = false;
      $('#results').classList.remove('show');
      lastResult = null;
    }
    // esc(): a parser error message can echo attacker-controlled input snippets
    // (e.g. from a crafted share link) — never inject it into innerHTML raw.
    toastMsg(`Compare failed: <span class="k">${esc(err.message)}</span>`);
    if (report) track('compare_error', { format: payload.format, extra: 'engine' });
  } finally {
    pendingCompares--;
    if (pendingCompares === 0) setBusy(false); // reset only when nothing is in flight
  }
}
$('#compareBtn').addEventListener('click', () => {
  input.runDetect(); // resolve any pending debounced detection synchronously, so a
  // fast Compare right after a paste doesn't run as plain Text under a JSON label.
  track('compare_click', { format: input.getPayload().format });
  doCompare({ scroll: true, report: true });
});

// Re-run in place (no scroll) — used by ignore-rules, the key column, and Format.
function rerun() {
  if ($('#results').classList.contains('show')) doCompare({ scroll: false, report: false });
}

function renderResult(result, { scroll = true, ignoreFields = [] } = {}) {
  // One model, read once: the stat cards' navigability, the stepper and the
  // exported findings all describe the same set of places, so they must not
  // each compute their own (G80 — the count and the position stay in units).
  // Local now: the findings row was the only other reader, and a module-level
  // binding that nothing shares is a binding that can go stale.
  const navModel = changeStops(result);
  changeFilter = null;
  $('#cruxSub').textContent = cruxSubtitle(result);
  $('#summary').innerHTML = summaryHtml(result, {
    navigable: navModel.categories.filter((c) => c.stopIndexes.length).map((c) => c.id),
    model: navModel,
    places: navModel.stops.length,
    filterable: canFilter(result),
  });
  animateCounts();
  changeNav.setModel(navModel);
  $('#cruxPanel').classList.remove('hidden');
  renderAnalysis(result, ignoreFields);
  updateScopeControls(result);
  renderDiff();
  const el = $('#results');
  el.classList.add('show');
  if (scroll) el.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
}

// The navigator owns resolving a place to a node, revealing it (a virtualized
// record list, a view that cannot show it) and highlighting it. It asks for a
// view change through this callback rather than reaching into main.js state.
// A ledger of rows can be narrowed to one kind of change; a document cannot.
// Text and prose are read in order, and hiding the lines between two edits would
// misrepresent the file rather than narrow a list — so those formats navigate
// only, and their cards say "Step through" instead of promising a filter.
function canFilter(result) {
  const kind = result?.render?.kind;
  return kind === 'records' || kind === 'structural';
}

let changeFilter = null;

const changeNav = createChangeNav({
  root: $('#cruxPanel'),
  body: $('#diffBody'),
  reduceMotion,
  onNeedView: (want) => {
    if (view === want) return false;
    setView(want);
    return true;
  },
  // Returns whether the diff was actually narrowed, so the navigator can show
  // the way back only when there is something to go back from.
  onFilter: (category) => {
    if (!lastResult || !canFilter(lastResult)) return false;
    const next = category === 'all' ? null : category;
    if (changeFilter === next) return Boolean(next);
    changeFilter = next;
    renderDiff();
    return Boolean(next);
  },
});

// ---- Change Analysis panel (spec §8.7) ----
// Session state, not result state: spec says the panel "never disappears
// mid-session". Once it has appeared for the current inputs it stays available
// through reruns, and if an ignore rule drops the comparison below threshold it
// collapses to a before/after line instead of vanishing under the cursor.
// `analysisBaseline` is the difference count it first appeared with — the "438"
// in "438 → 147". All three reset when the INPUTS change (onChange below); an
// ignore-rule or key-column rerun deliberately does not reset them.
function renderAnalysis(result, ignoreFields) {
  const panel = $('#analysisPanel');
  const analysis = analyzeChanges(result, { ignoreFields });
  if (!analysis || (!analysis.meetsThreshold && !analysisShown)) {
    panel.hidden = true;
    return;
  }
  if (analysis.meetsThreshold && !analysisShown) {
    analysisShown = true;
    analysisBaseline = analysis.differences;
    // Which arm of "≥150 records OR ≥50 differences" fired is the one measurement
    // that says whether those two numbers are set anywhere near right.
    track('analysis_shown', {
      kind: analysis.records.total >= ANALYSIS_THRESHOLDS.minRecords ? 'records' : 'differences',
    });
  }
  const below = !analysis.meetsThreshold;
  $('#analysisBody').innerHTML = analysisHtml(analysis);
  $('#analysisLead').textContent = analysisLead(analysis, below ? analysisBaseline : null);
  // Only force the open state ON THE TRANSITION, so a reader who collapsed the
  // panel themselves doesn't have it reopened by the next rerun.
  if (below !== analysisBelow) panel.open = !below;
  analysisBelow = below;
  panel.hidden = false;
  restoreAnalysisFocus();
}

// ---- one-click ignore / undo (spec §8.7, §8.10 "Signature 2") ----
// The panel owns NO ignore state of its own: it writes the same comma-separated
// Ignore fields rule the Options popover exposes, then re-runs the real
// comparison. So the rule stays visible and editable where users already expect
// it, undo is just removing the token again, and a share link carries the choice.
const ignoreTokens = () => $('#ignoreFields').value.split(',').map((t) => t.trim()).filter(Boolean);

function setIgnoreTokens(tokens, focusField) {
  $('#ignoreFields').value = tokens.join(', ');
  analysisFocus = focusField; // restore focus after the panel re-renders (below)
  rerun();
}

$('#analysisBody').addEventListener('click', (e) => {
  const add = e.target.closest('[data-ignore-field]');
  const addAll = e.target.closest('[data-ignore-all]');
  const remove = e.target.closest('[data-unignore-field]');
  if (!add && !addAll && !remove) return;
  // Combined action: clicking the rows one at a time cannot reach this result —
  // ignoring the first leaves the second as the only changed field, where the
  // contrast rule stops listing it (REL-067) — so the combined outcome needs its
  // own control or it is an outcome the UI advertises and cannot produce. Undo is
  // per field, from the chips, so it stays reversible one step at a time.
  const fields = addAll ? addAll.dataset.ignoreAll.split(',').filter(Boolean) : [add ? add.dataset.ignoreField : remove.dataset.unignoreField];
  const tokens = ignoreTokens();
  if (add || addAll) {
    for (const field of fields) if (!tokens.includes(field)) tokens.push(field);
  } else {
    tokens.splice(0, tokens.length, ...tokens.filter((t) => t !== fields[0]));
  }
  // THAT one was accepted or undone, never WHICH: a column name is user data, and
  // `analysis_ignore` carries a closed kind enum so the collector could not store
  // one even if a future edit passed it.
  if (add) track('analysis_ignore', { kind: 'one' });
  else if (addAll) track('analysis_ignore', { kind: 'all' });
  else track('analysis_undo', {});
  setIgnoreTokens(tokens, fields[fields.length - 1]);
});

$('#analysisBody').addEventListener('toggle', (e) => {
  const details = e.target.closest?.('.an-detail, .an-more');
  if (!details || !details.open) return;
  const label = details.querySelector('summary')?.textContent || '';
  const section = details.classList.contains('an-more')
    ? 'fields'
    : /^Ignore fields/.test(label)
      ? 'ignore'
      : /^How this/.test(label)
        ? 'rules'
        : /^Schema/.test(label)
          ? 'schema'
          : null;
  if (!section || analysisOpened.has(section)) return;
  analysisOpened.add(section);
  track('analysis_expanded', { kind: section });
}, true); // `toggle` does not bubble — listen in the capture phase

// Re-rendering the body replaces the button that was just pressed, which would
// drop focus to <body> mid-interaction. Put it back on whatever now represents
// the same field — its Undo after an ignore, its Ignore after an undo.
let analysisFocus = null;
function restoreAnalysisFocus() {
  if (!analysisFocus) return;
  const field = analysisFocus;
  analysisFocus = null;
  // Match on the attribute VALUE rather than building a selector from a field
  // name: a name is user data, and the no-CSS.escape fallback threw on a newline.
  const buttons = [...document.querySelectorAll('#analysisBody [data-unignore-field], #analysisBody [data-ignore-field]')];
  const next =
    buttons.find((b) => b.dataset.unignoreField === field) ||
    buttons.find((b) => b.dataset.ignoreField === field) ||
    $('#analysisPanel > summary');
  next?.focus();
}

function resetAnalysisSession() {
  analysisOpened.clear();
  analysisShown = false;
  analysisBaseline = null;
  analysisBelow = false;
}

// The scope switcher (Changed only | All rows) appears only for large record
// sets; for anything smaller we show every row and the toggle stays hidden.
function updateScopeControls(result) {
  const r = result || lastResult;
  const rows = r && !r.render.source && r.render?.kind === 'records' ? (r.added || 0) + (r.removed || 0) + (r.modified || 0) + (r.unchanged || 0) : 0;
  scopeActive = rows >= SCOPE_MIN_ROWS;
  $('#scopeSeg').style.display = scopeActive ? 'flex' : 'none';
  if (!scopeActive) scope = 'changed';
}

// Record scale for `compare_size`. Record-based results count logical records;
// everything else counts its own change units, so the buckets stay comparable
// within a format rather than across them.
function sizeBucket(result) {
  const n =
    result.render?.kind === 'records'
      ? (result.added || 0) + (result.removed || 0) + (result.modified || 0) + (result.unchanged || 0)
      : (result.changes?.length ?? 0);
  if (n < 100) return 'xs';
  if (n < 1000) return 's';
  if (n < 10000) return 'm';
  if (n < 100000) return 'l';
  return 'xl';
}

const reportedRenderErrors = new WeakSet();
function withRenderErrorBeacon(render) {
  try {
    return render();
  } catch (err) {
    // A render error may bubble through nested guarded paths; report the Error
    // object once, and never serialize its message or stack.
    if (!err || typeof err !== 'object' || !reportedRenderErrors.has(err)) {
      if (err && typeof err === 'object') reportedRenderErrors.add(err);
      trackClientError('render_throw');
    }
    throw err;
  }
}

function renderDiff() {
  return withRenderErrorBeacon(() => renderDiffUnsafe());
}

function renderDiffUnsafe() {
  if (!lastResult) return;
  const kind = lastResult.render?.kind;
  const rv = effectiveView();
  $('#expandJsonBtn').hidden = !(lastResult.render.expandedSource && view === 'sxs');
  const out =
    lastResult.render.source && view === 'sxs' && !changeFilter
      ? renderStructuredSource(lastResult, lastMeta, { stacked: NARROW.matches, expanded: expandJsonView })
      : kind === 'prose'
      ? renderProse(lastResult, rv, lastMeta)
      : kind === 'structural'
        ? renderStructural(lastResult, rv, lastMeta, { filter: changeFilter })
        : kind === 'records'
          ? renderRecords(lastResult, rv, lastMeta, { scope: scopeActive ? scope : 'all', big: scopeActive, layout: recordLayoutFor(lastResult.format, view, RECORD_DESKTOP.matches), filter: changeFilter })
          : renderText(lastResult, rv, lastMeta);
  const body = $('#diffBody');
  unmountRecordVirtualization();
  body.innerHTML = out.html;
  unmountRecordVirtualization = mountRecordVirtualization(body, lastResult);
  body.classList.toggle('wrap', wrap);
  lastRibbons = out.ribbons || [];
  requestAnimationFrame(() => withRenderErrorBeacon(() => drawRibbons($('#ribbon'), $('.diff-card'), lastRibbons)));
  // A view/scope switch rebuilt the DOM under the reader — put the highlight
  // back on the place they were standing instead of dropping them at the top.
  changeNav?.restore();
}

// The saved report's diff: the same view the reader chose, but with no category
// filter, no highlight and no virtualization — a virtualized All-rows UI mounts
// only a small scrolling window, and the file must hold the complete selected
// scope. Kept beside renderDiffUnsafe so the two dispatches are read together.
function reportDiffHtml() {
  const kind = lastResult.render?.kind;
  const rv = effectiveView();
  if (lastResult.render.source && view === 'sxs') return renderStructuredSource(lastResult, lastMeta, { stacked: NARROW.matches, expanded: expandJsonView }).html;
  if (kind === 'prose') return renderProse(lastResult, rv, lastMeta).html;
  if (kind === 'structural') return renderStructural(lastResult, rv, lastMeta).html;
  if (kind === 'records') {
    return renderRecords(lastResult, rv, lastMeta, {
      scope: scopeActive ? scope : 'all',
      big: scopeActive,
      virtualize: false,
      layout: recordLayoutFor(lastResult.format, view, RECORD_DESKTOP.matches),
    }).html;
  }
  return renderText(lastResult, rv, lastMeta).html;
}

// Re-render when crossing the narrow breakpoint (Side-by-side ⇄ Unified).
NARROW.addEventListener('change', () => {
  if (lastResult) renderDiff();
});
RECORD_DESKTOP.addEventListener('change', () => { if (lastResult) renderDiff(); });

function animateCounts() {
  const reduce = reduceMotion();
  const DURATION = 650; // total roll-up time, independent of the target value
  const MAX_STEPS = 26;
  $('#summary')
    .querySelectorAll('.n')
    .forEach((el) => {
      const target = parseInt(el.dataset.n, 10);
      if (reduce || !Number.isFinite(target) || target <= 1) {
        el.textContent = Number.isFinite(target) ? String(target) : '0';
        return;
      }
      // Scale the increment so a big diff finishes in the same ~650 ms as a small
      // one (and never leaves a slow interval running on a re-rendered node).
      const steps = Math.min(target, MAX_STEPS);
      const inc = Math.max(1, Math.ceil(target / steps));
      let v = 0;
      const iv = setInterval(() => {
        v = Math.min(target, v + inc);
        el.textContent = String(v);
        if (v >= target) clearInterval(iv);
      }, DURATION / steps);
    });
}

function renderStubResult(r) {
  $('#analysisPanel').hidden = true;
  // Nothing to control: no counts, no places, no matching to describe. The
  // panel is hidden rather than left standing empty, and the stub note below
  // says why (no dead chrome — §20.5).
  $('#summary').innerHTML = '';
  $('#cruxPanel').classList.add('hidden');
  const delta = r.identical ? 'Inputs are identical.' : `~<b>${r.addedLines}</b> added · ~<b>${r.removedLines}</b> removed lines <span style="color:var(--faint)">(naïve stub)</span>.`;
  $('#diffBody').innerHTML = `<div class="cap" style="margin:14px 16px"><b style="color:var(--amber)">Engine stub.</b> ${r.leftLines} → ${r.rightLines} lines · format <b>${esc(String(r.format))}</b>. ${delta}</div>`;
  $('#ribbon').innerHTML = '';
  lastRibbons = [];
  $('#results').classList.add('show');
  $('#results').scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
}

// ---- Options popover: ignore rules re-run instantly, client-side (spec §8.3) ----
['optWhitespace', 'optCase', 'optKeyOrder'].forEach((id) => $('#' + id).addEventListener('change', rerun));
let ignoreTimer;
$('#ignoreFields').addEventListener('input', () => {
  clearTimeout(ignoreTimer);
  ignoreTimer = setTimeout(() => {
    // Debounced, so this counts a settled rule rather than each keystroke. The
    // rule's CONTENT is field names — user data — so only whether one is in force
    // travels, as a bucket.
    track('ignore_rule_applied', { extra: ignoreTokens().length ? 'set' : 'cleared' });
    rerun();
  }, 400);
});
$('#optDone').addEventListener('click', () => closePops());
$('#keycolWrap').addEventListener('change', rerun);

// ---- detection pill: click-to-override + back-to-auto (spec §8.1) ----
// Mark which entry is active: "Auto-detect" when not overridden, else the chosen
// format — so the menu shows the current state and offers a way back to auto.
function markActiveDetOpt() {
  const st = input.state;
  // Parquet is the one format you cannot choose for pasted text — it only exists
  // once a file is decoded. So while a Parquet document is open the menu reports it
  // as the current format; the rest of the time the entry stays aria-disabled and
  // says how to get there, instead of offering an override that cannot be applied.
  const parquet = st.format === 'parquet';
  document.querySelectorAll('#detMenu .det-opt').forEach((o) => {
    const f = o.dataset.fmt;
    const active = parquet ? f === 'parquet' : f === 'auto' ? !st.overridden : st.overridden && f === st.format;
    o.setAttribute('aria-current', String(active));
    if (f === 'parquet') o.setAttribute('aria-disabled', String(!parquet));
  });
}
const detOpts = () => [...document.querySelectorAll('#detMenu .det-opt')];
function openDetMenu() {
  markActiveDetOpt();
  const willOpen = !$('#detMenu').classList.contains('open');
  togglePop('#detMenu');
  if (willOpen) {
    const opts = detOpts();
    (opts.find((o) => o.getAttribute('aria-current') === 'true') || opts[0])?.focus();
  }
}
$('#detected').addEventListener('click', (e) => {
  e.stopPropagation();
  openDetMenu();
});
$('#detected').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
    e.preventDefault();
    openDetMenu();
  }
});
// Arrow-key navigation within the format menu (Escape handled globally).
$('#detMenu').addEventListener('keydown', (e) => {
  const opts = detOpts();
  const i = opts.indexOf(document.activeElement);
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    opts[(i + 1) % opts.length]?.focus();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    opts[(i - 1 + opts.length) % opts.length]?.focus();
  } else if (e.key === 'Home') {
    e.preventDefault();
    opts[0]?.focus();
  } else if (e.key === 'End') {
    e.preventDefault();
    opts[opts.length - 1]?.focus();
  }
});
detOpts().forEach((b) =>
  b.addEventListener('click', () => {
    if (b.getAttribute('aria-disabled') === 'true') return; // listed for completeness, not selectable
    closePops();
    input.setFormat(b.dataset.fmt, { manual: true });
    $('#detected').focus(); // return focus to the trigger after choosing
    rerun();
  }),
);

// ---- scope switcher (Changed only | All rows) ----
$('#scopeSeg')
  .querySelectorAll('button')
  .forEach((b) =>
    b.addEventListener('click', () => {
      scope = b.dataset.scope;
      $('#scopeSeg').querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      track('scope_switch', { extra: scope });
      if (lastResult) renderDiff();
    }),
  );

// ---- Try with sample: load the demo and run it, so the payoff is immediate ----
$('#sampleBtn').addEventListener('click', () => {
  track('sample_try', {});
  input.loadSample();
  doCompare({ scroll: true, report: true });
});

// ---- Format / normalize pre-pass ----
$('#fmtBtn').addEventListener('click', () => {
  const fmt = input.getPayload().format;
  if (fmt === 'parquet') { toastMsg('Parquet is viewed as decoded records — use the pane viewer, or the full-page <a href="/parquet-viewer.html">Parquet viewer</a>, to inspect or export rows.'); return; }
  $('#leftTa').value = formatContent($('#leftTa').value, fmt);
  $('#rightTa').value = formatContent($('#rightTa').value, fmt);
  input.runDetect();
  toastMsg('Input prettified — <span class="k">the engine normalizes anyway</span>, this cleans the raw view.');
  rerun();
});

// ---- view switcher ----
// Extracted so the change navigator can reach a place the current view cannot
// show (Raw has no record keys or leaf paths) by moving to Unified, which lists
// every change for every format — rather than failing silently.
function setView(next, { track: report = true } = {}) {
  view = next;
  const buttons = $('#viewSeg').querySelectorAll('button');
  buttons.forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.view === next)));
  $('#segHint').textContent = view === 'raw' ? "the fallback when semantic parsing isn't possible" : 'semantic highlighting stays on in every layout';
  if (report) track('view_switch', { extra: view });
  if (lastResult) renderDiff();
}

$('#viewSeg')
  .querySelectorAll('button')
  .forEach((b) => b.addEventListener('click', () => setView(b.dataset.view)));

$('#expandJsonBtn').addEventListener('click', () => {
  expandJsonView = !expandJsonView;
  $('#expandJsonBtn').setAttribute('aria-pressed', String(expandJsonView));
  if (lastResult) renderDiff();
});

// ---- wrap toggle (align panes / avoid independent horizontal scroll) ----
$('#wrapBtn').addEventListener('click', () => {
  wrap = !wrap;
  $('#wrapBtn').setAttribute('aria-pressed', String(wrap));
  $('#diffBody').classList.toggle('wrap', wrap);
  track('wrap_toggle', { extra: wrap ? 'on' : 'off' });
  if (lastResult && lastRibbons.length) requestAnimationFrame(() => withRenderErrorBeacon(() => drawRibbons($('#ribbon'), $('.diff-card'), lastRibbons)));
});

let resizeTimer;
addEventListener('resize', () => {
  if (!lastResult || !lastRibbons.length) return;
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => withRenderErrorBeacon(() => drawRibbons($('#ribbon'), $('.diff-card'), lastRibbons)), 80);
});

// ---- popovers / menus (keyboard-accessible: aria-expanded, Escape, arrows) ----
const POP_MAP = { '#detMenu': '#detected', '#optPop': '#optBtn', '#sharePop': '#shareBtn', '#exportPop': '#exportBtn' };
function closePops() {
  document.querySelectorAll('.pop').forEach((p) => p.classList.remove('open'));
  Object.values(POP_MAP).forEach((t) => $(t)?.setAttribute('aria-expanded', 'false'));
}
function togglePop(id) {
  const pop = $(id);
  const willOpen = !pop.classList.contains('open');
  closePops();
  if (willOpen) {
    pop.classList.add('open');
    if (POP_MAP[id]) $(POP_MAP[id]).setAttribute('aria-expanded', 'true');
  }
}
$('#optBtn').addEventListener('click', (e) => {
  e.stopPropagation();
  togglePop('#optPop');
});
$('#shareBtn').addEventListener('click', (e) => {
  e.stopPropagation();
  togglePop('#sharePop');
});
$('#exportBtn').addEventListener('click', (e) => {
  e.stopPropagation();
  togglePop('#exportPop');
});
const POP_TRIGGERS = '#detected, #optBtn, #shareBtn, #exportBtn';
document.addEventListener('click', (e) => {
  if (!e.target.closest('.pop') && !e.target.closest(POP_TRIGGERS)) closePops();
});
// Escape closes any open popover/menu and returns focus to its trigger.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const open = document.querySelector('.pop.open');
  if (!open) return;
  const trigger = POP_MAP['#' + open.id];
  closePops();
  if (trigger) $(trigger).focus();
});

$('#copyLinkBtn').addEventListener('click', async () => {
  closePops();
  if (input.getPayload().format === 'parquet') { toastMsg('Parquet files cannot be included in a share link. Download an HTML report or JSON change list.'); return; }
  const enc = encodeState(buildPayload());
  track('share_created', { extra: enc.ok ? 'link' : 'too-large' });
  if (!enc.ok) {
    toastMsg(`Too large to share as a link (~${(enc.size / 1024).toFixed(1)} KB) — <span class="k">export instead</span>.`);
    return;
  }
  location.hash = enc.fragment;
  try {
    // Two flavours where the clipboard allows it: the fragment carries the whole
    // comparison, so the link is long by design — this keeps a reader from having
    // to look at it. Plain text stays the bare URL for terminals and mail.
    const how = await copyShareLink(enc.url);
    toastMsg(
      how === 'rich'
        ? 'Link copied — it pastes as a tidy link, and <span class="k">the diff lives in the URL fragment</span>: nothing stored server-side.'
        : 'Link copied — <span class="k">the diff lives in the URL fragment</span>, nothing stored server-side.',
    );
  } catch {
    toastMsg('Link is in your address bar — <span class="k">copy it</span> (clipboard was blocked).');
  }
});

document.querySelectorAll('.exp-item').forEach((b) =>
  b.addEventListener('click', () => {
    closePops();
    if (!$('#results').classList.contains('show')) {
      toastMsg('Run a comparison first, then download.');
      return;
    }
    // Dispatch on data-exp, not on the label: the visible text is copy and has
    // already been reworded once (Export → Download, 19 Sep 2026).
    const t = b.dataset.exp;
    if (t === 'json') {
      if (!lastResult) {
        toastMsg('Change list needs a semantic result — this format falls back to a line diff. <span class="k">Download the unified patch</span>.');
        return;
      }
      track('export', { extra: 'json' });
      const r = lastResult;
      const json = changeListJson({
        format: r.format,
        keyColumn: r.keyColumn,
        summary: { added: r.added || 0, removed: r.removed || 0, modified: r.modified || 0, unchanged: r.unchanged || 0, moved: r.moved || 0, typeChanges: r.typeChanges || 0 },
        changes: r.changes,
      });
      download('cruxdiff-changes.json', json, 'application/json');
      toastMsg('Change list (.json) generated <span class="k">locally</span> — the semantic result, nothing uploaded.');
    } else if (t === 'patch') {
      if (lastResult?.format === 'parquet') { toastMsg('Text patches cannot recreate binary Parquet files. Download an HTML report or JSON change list.'); return; }
      track('export', { extra: 'patch' });
      download('cruxdiff.patch.diff', unifiedPatch(lastMeta.leftLabel, lastMeta.rightLabel, $('#leftTa').value, $('#rightTa').value), 'text/plain');
      toastMsg('Unified patch generated <span class="k">locally</span> — nothing uploaded.');
    } else {
      track('export', { extra: 'html' });
      const html = htmlReport({
        title: `${lastMeta.leftLabel || 'original'} ↔ ${lastMeta.rightLabel || 'changed'}`,
        tokensCss,
        sourceCss,
        recordTableCss,
        theme: document.documentElement.dataset.theme,
        // Every section is rendered from the RESULT, never copied out of the
        // page: the live cards are buttons mid-count-up and the live diff carries
        // the category filter and the navigator's highlight (G44/G113 — a
        // screen's state never decides a file's).
        summaryHtml: lastResult ? summaryHtml(lastResult) : '',
        // Findings at CRUX_CAP (14) plus an overflow line naming the rest: the
        // diff below is the complete record, and a findings list the length of a
        // 250k-row diff would double the file to say it twice.
        cruxHtml: lastResult ? cruxHtml(lastResult) : '',
        diffHtml: lastResult ? reportDiffHtml() : $('#diffBody').innerHTML,
      });
      download('cruxdiff-report.html', html, 'text/html');
      toastMsg('HTML report generated <span class="k">locally</span> — nothing uploaded.');
    }
  }),
);

// ---- restore a shared diff from the URL fragment ----
function setOpt(id, value) {
  const el = $('#' + id);
  if (el) el.checked = !!value;
}
function applyState(s) {
  $('#leftTa').value = s.left || '';
  $('#rightTa').value = s.right || '';
  $('#leftFile').textContent = '';
  $('#rightFile').textContent = '';
  const o = s.options || {};
  setOpt('optWhitespace', o.ignoreWhitespace);
  setOpt('optCase', o.ignoreCase);
  setOpt('optKeyOrder', o.ignoreKeyOrder);
  // ignoreFields comes from an attacker-authored fragment — coerce defensively
  // (a non-array would throw on .join and abort the whole restore).
  $('#ignoreFields').value = Array.isArray(o.ignoreFields) ? o.ignoreFields.join(', ') : String(o.ignoreFields || '');
  input.setFormat(s.format || 'text', { manual: true });
  if (s.keyColumn) {
    const sel = $('#keycolWrap select');
    if (sel && [...sel.options].some((op) => op.value === s.keyColumn)) sel.value = s.keyColumn;
  }
  doCompare({ scroll: true, report: true });
}

const restored = decodeState(location.hash);
if (restored) {
  track('share_open', {});
  applyState(restored);
} else {
  // A share fragment that failed to decode (too long, corrupt, wrong shape) is
  // dropped from the address bar, so a reload or a re-share does not replay it.
  // The reader is told, or they meet an empty tool with no idea why. Every link
  // this site makes decodes, so a failure means the URL was altered on the way —
  // most often cut short by a chat app or mail client — and only the sender can
  // fix that. Fixed text: nothing from the fragment is echoed back.
  if (location.hash.startsWith('#d=')) {
    history.replaceState(null, '', location.pathname + location.search);
    showNotice({
      title: 'This share link couldn’t be opened',
      message: 'It was cut short or changed on its way to you, so nothing from it was loaded. Ask the sender to copy the link again and resend it.',
    });
  }
  // Landing pages deep-link with ?format=json|csv|text to preselect the tab.
  const wanted = new URLSearchParams(location.search).get('format');
  if (wanted && ['text', 'json', 'csv'].includes(wanted)) input.setFormat(wanted, { manual: true });
}

// ---- PWA service worker (offline after first load) ----
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  addEventListener('load', () => navigator.serviceWorker.register('/service-worker.js').catch(() => {}));
}
addEventListener('appinstalled', () => track('pwa_install', {}));

trackPageView('home');
trackVisitRecency();
